import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { NotificationState } from 'components/shared/Notification';
import useAIJobChannel from 'hooks/useAIJobChannel';
import {
  getAIJobNotificationTitle,
  isOnJobLoadingPage as isOnJobReturnUrl,
  updateJobbableForAIJob,
} from 'lib/aiJobHelpers';
import { dateAtTimeNoRelative } from 'lib/date';
import AIJob from 'models/AIJob';
import {
  type AIJobNotificationContext,
  AIJobNotificationType,
  type TrackedJob,
  useAIJobNotificationStore,
} from 'stores/aiJobNotificationStore';
import { useNotificationActions } from 'stores/notificationStore';

type JobSubscriberProps = {
  job: TrackedJob;
  isActive: boolean;
  onJobUpdate: (job: AIJob) => void;
};

/**
 * Subscribes to a single AI job's WebSocket channel.
 * Extracted as a component so each job gets its own useAIJobChannel hook.
 */
const JobSubscriber = ({ job, isActive, onJobUpdate }: JobSubscriberProps) => {
  useAIJobChannel({
    aiJobId: isActive ? job.id : undefined,
    onMessageReceived: onJobUpdate,
  });

  return null;
};

type AIJobTrackerProps = {
  context: AIJobNotificationContext;
  trackingKey: string;
};

/**
 * Component that manages a notification lifecycle for one or more AI jobs.
 * The notification stays alive until all jobs have finished processing.
 */
const AIJobTracker = ({ context, trackingKey }: AIJobTrackerProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    untrackAIJob,
    dismissJobNotification,
    addJobNotificationMapping,
    removeJobNotificationMapping,
    dismissedProcessingJobs,
    jobNotificationMap,
    clearDismissedProcessingJob,
  } = useAIJobNotificationStore();
  const { addNotification, updateNotification, removeNotification } = useNotificationActions();

  const isOnReturnUrl = useMemo(() => isOnJobReturnUrl(location.pathname, context), [location.pathname, context]);

  const notificationId = useMemo(() => jobNotificationMap.get(trackingKey), [jobNotificationMap, trackingKey]);

  const wasDismissedWhileProcessing = useMemo(
    () => dismissedProcessingJobs.has(trackingKey),
    [dismissedProcessingJobs, trackingKey]
  );

  // Track processing state per job
  const [processingJobs, setProcessingJobs] = useState<Set<string>>(() => new Set(context.jobs.map((j) => j.id)));
  const allJobsDone = processingJobs.size === 0;

  // Track whether any job has failed
  const hasFailedJobRef = useRef(false);

  // Handle job status updates
  const handleJobUpdate = useCallback(
    (job: AIJob) => {
      updateJobbableForAIJob(job, context.jobbableId, context.jobType);

      if (!job.isProcessing) {
        if (!job.isCompleted) hasFailedJobRef.current = true;
        setProcessingJobs((prev) => {
          const next = new Set(prev);
          next.delete(job.id);
          return next;
        });
      }
    },
    [context]
  );

  // When all jobs finish, update the notification
  useEffect(() => {
    if (!allJobsDone) return;

    const store = useAIJobNotificationStore.getState();
    const currentNotificationId = store.jobNotificationMap.get(trackingKey);
    const wasDismissed = store.dismissedProcessingJobs.has(trackingKey);
    const freshContext = store.trackedJobs.get(trackingKey) ?? context;
    const currentIsOnReturnUrl = isOnJobReturnUrl(window.location.pathname, freshContext);

    const succeeded = !hasFailedJobRef.current;

    const payload = {
      state: succeeded ? NotificationState.SUCCESS : NotificationState.FAILURE,
      title: getAIJobNotificationTitle(freshContext, succeeded ? 'completed' : 'failed'),
      timestamp:
        freshContext.jobType === AIJobNotificationType.WORKSPACE_SUMMARY ||
        freshContext.jobType === AIJobNotificationType.ADMISSION_SUMMARY
          ? undefined
          : (dateAtTimeNoRelative(new Date()) as string),
      onActionClick: () => navigate(freshContext.returnUrl),
      onDismiss: () => handleDismiss(),
      actionSuccessText:
        freshContext.jobType === AIJobNotificationType.AUTHORIZATION_COMPARISON ? 'View comparison' : 'View Summary',
    };

    if (currentIsOnReturnUrl) {
      untrackAIJob(trackingKey);
    } else if (wasDismissed) {
      const newNotificationId = addNotification(payload);
      addJobNotificationMapping(trackingKey, newNotificationId);
      clearDismissedProcessingJob(trackingKey);
    } else if (currentNotificationId) {
      updateNotification(currentNotificationId, payload);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allJobsDone]);

  // Create or remove notification based on route
  useEffect(() => {
    if (isOnReturnUrl) {
      // User is on the loading page, remove notification if it exists
      if (notificationId) {
        removeNotification(notificationId);
        if (!allJobsDone) removeJobNotificationMapping(trackingKey);
        else untrackAIJob(trackingKey);
      }
    } else {
      // User is NOT on loading page, ensure notification exists (if not dismissed)
      if (!notificationId && !wasDismissedWhileProcessing && !allJobsDone) {
        const newNotificationId = addNotification({
          state: NotificationState.PROCESSING,
          title: getAIJobNotificationTitle(context, 'processing'),
          timestamp: undefined,
          onActionClick: undefined,
          onDismiss: () => handleDismiss(),
        });

        addJobNotificationMapping(trackingKey, newNotificationId);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOnReturnUrl, notificationId, wasDismissedWhileProcessing, allJobsDone, context]);

  const handleDismiss = useCallback(() => {
    const store = useAIJobNotificationStore.getState();
    const currentNotificationId = store.jobNotificationMap.get(trackingKey);
    const isStillProcessing = processingJobs.size > 0;

    dismissJobNotification(trackingKey, isStillProcessing);

    if (currentNotificationId) {
      removeNotification(currentNotificationId);
      removeJobNotificationMapping(trackingKey);
    }
  }, [trackingKey, processingJobs, dismissJobNotification, removeNotification, removeJobNotificationMapping]);

  return (
    <>
      {context.jobs.map((job) => (
        <JobSubscriber key={job.id} job={job} isActive={processingJobs.has(job.id)} onJobUpdate={handleJobUpdate} />
      ))}
    </>
  );
};

export const AIJobNotificationManager = () => {
  const { trackedJobs } = useAIJobNotificationStore();

  const trackedJobsArray = useMemo(() => Array.from(trackedJobs.entries()), [trackedJobs]);

  return (
    <>
      {trackedJobsArray.map(([key, context]) => (
        <AIJobTracker key={key} trackingKey={key} context={context} />
      ))}
    </>
  );
};
